import { Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useEffect, useState } from "react";
import { Employee } from "../../interfaces/Customer";
import { EducationOptions } from "../../interfaces/Provider";
import { HighlightedTextField } from "../../services/customElements";
import { extractBirthDateFromPesel, extractMunicipalityCode, extractSexFromPesel, isFloat } from "../../services/utils";


const educationValues = Object.values(EducationOptions);
const EmployeesList: React.FC<{ employees: Employee[]; setEmployees: (employees: Employee[]) => void; setAllEmployeesCorrect: (correct: boolean) => void; employeesChanged: boolean }> = ({ employees, setEmployees, setAllEmployeesCorrect, employeesChanged }) => {
    const [shiftCounts, setShiftCounts] = useState<string[]>(employees.map(employee => employee.shiftCount.toString().replace(".", ",")));

    useEffect(() => {
        setShiftCounts(employees.map(employee => employee.shiftCount.toString().replace(".", ",")));
    }, [employeesChanged]);


    const handleShiftCountChange = (index: number, value: string) => {
        const updatedEmployees = [...employees];
        setShiftCounts(prev => {
            const newShiftCounts = [...prev];
            newShiftCounts[index] = value;
            return newShiftCounts;
        });
        if (isFloat(value)) {
            updatedEmployees[index].shiftCount = parseFloat(value.replace(",", "."));
        }
        validateAndSetEmployees(updatedEmployees);
    }

    const validateShiftCounts = (shiftCounts: string[]) => {
        return shiftCounts.map(shiftCount => {
            if (isFloat(shiftCount)) {
                return "";
            }
            return "Wymiar etatu musi być liczbą";
        });
    }

    const validateEmployeeFields = (employee: any) => {
        const errors: { [key: string]: string } = {};

        if (!employee.activeForCoaching) {
            return errors; // No validation needed if not active for coaching
        }

        // Validate PESEL
        if (!/^\d{11}$/.test(employee.pesel)) {
            errors.pesel = "PESEL musi zawierać dokładnie 11 cyfr.";
        }

        // Validate sex
        if (!/^[mk]$/.test(employee.sex)) {
            errors.sex = "Dozwolone m/k";
        }

        // Validate municipalityCode
        if (!/^\d{6} \d{1}$/.test(employee.address.municipalityCode)) {
            errors.municipalityCode = 'Kod gminy musi składać się z 7 cyfr. Ostatniej oddzielonej spacją';
        }

        if (!/^\d{5}$/.test(employee.address.zipCode)) {
            errors.zipCode = 'Kod pocztowy powinien być w formacie XXXXX.';
        }

        const validVoivodeships = [
            'dolnośląskie', 'kujawsko-pomorskie', 'lubelskie', 'lubuskie',
            'łódzkie', 'małopolskie', 'mazowieckie', 'opolskie', 'podkarpackie',
            'podlaskie', 'pomorskie', 'śląskie', 'świętokrzyskie', 'warmińsko-mazurskie',
            'wielkopolskie', 'zachodniopomorskie'
        ];
        if (!validVoivodeships.includes(employee.address.voivodeship.toLowerCase())) {
            errors.voivodeship = 'Województwo musi być jednym z województw Polski.';
        }

        if (!employee.address.streetName) {
            errors.streetName = 'Wypełnij nazwę ulicy.';
        }

        if (!employee.address.streetNumber) {
            errors.streetNumber = 'Wypełnij numer ulicy.';
        }

        if (!employee.address.city) {
            errors.city = 'Wypełnij miasto.';
        }

        if (!employee.address.district) {
            errors.district = 'Wypełnij powiat.';
        }

        if (!employee.address.municipality) {
            errors.municipality = 'Wypełnij gminę.';
        }

        if (!employee.name) {
            errors.name = 'Wypełnij imię.';
        }

        if (!employee.surname) {
            errors.surname = 'Wypełnij nazwisko.';
        }

        return errors;
    }

    const checkIfEmployeesCorrect = (checkedEmployees: Employee[]) => {
        const correct = checkedEmployees.every(employee => {
            const errors = validateEmployeeFields(employee);
            return Object.keys(errors).length === 0;
        });
        setAllEmployeesCorrect(correct);
    }

    const validateAndSetEmployees = (updatedEmployees: Employee[]) => {
        checkIfEmployeesCorrect(updatedEmployees);
        setEmployees(updatedEmployees);
    }

    useEffect(() => {
        checkIfEmployeesCorrect(employees);
    }, [employees]);

    const updateComputedFields = (employee: Employee) => {
        if (employee.pesel.length == 11) {
            const birthDate = extractBirthDateFromPesel(employee.pesel);
            const sex = extractSexFromPesel(employee.pesel);
            employee.sex = sex || employee.sex;
            employee.birthDate = birthDate || employee.birthDate
        }
        if (employee.address.municipality.length > 0) {
            const municipalityCode = extractMunicipalityCode(employee.address.district, employee.address.municipality);
            employee.address.municipalityCode = municipalityCode || employee.address.municipalityCode;
        }
        return employee;
    }


    return (
        <Box sx={{ gridColumn: 'span 4', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h3>Pracownicy</h3>
            {employees.map((employee, index) => (
                <Box key={index} sx={{ display: 'flex', marginBottom: 2 }}>
                    <Box component="form" sx={{ width: '100%', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, padding: 0 }}>
                        <DatePicker
                            label="Zatrudniony OD"
                            value={dayjs(employee.employedSince)}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].employedSince = e?.format('YYYY-MM-DD') || "";
                                validateAndSetEmployees(updatedEmployees);
                            }
                            }
                        />
                        <DatePicker
                            label="Zatrudniony DO"
                            value={dayjs(employee.employedUntil)}
                            disabled={employee.employedIndefinitely}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].employedUntil = e?.format('YYYY-MM-DD') || "";
                                validateAndSetEmployees(updatedEmployees);
                            }
                            }
                        />
                        <TextField
                            label="Wymiar etatu"
                            value={shiftCounts[index]}
                            error={!!validateShiftCounts(shiftCounts)[index]}
                            helperText={validateShiftCounts(shiftCounts)[index]}
                            fullWidth
                            onChange={(e) => {
                                handleShiftCountChange(index, e.target.value);
                            }}
                        />
                        <FormControl>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={employees[index].activeForCoaching}
                                        onChange={(e) => {
                                            const updatedEmployees = [...employees];
                                            updatedEmployees[index].activeForCoaching = e.target.checked;
                                            validateAndSetEmployees(updatedEmployees);
                                        }}
                                    />
                                }
                                label="Wysyłany na szkolenie"
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="primary"
                                        checked={employees[index].employedIndefinitely}
                                        onChange={(e) => {
                                            const updatedEmployees = [...employees];
                                            updatedEmployees[index].employedIndefinitely = e.target.checked;
                                            validateAndSetEmployees(updatedEmployees);
                                        }}
                                    />
                                }
                                label="Zatrudniony na czas nieokreślony"
                            />
                        </FormControl>
                        <TextField
                            label="Imię"
                            disabled={!employee.activeForCoaching}
                            value={employee.name}
                            error={!!validateEmployeeFields(employee).name}
                            helperText={validateEmployeeFields(employee).name}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].name = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Nazwisko"
                            disabled={!employee.activeForCoaching}
                            value={employee.surname}
                            error={!!validateEmployeeFields(employee).surname}
                            helperText={validateEmployeeFields(employee).surname}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].surname = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <HighlightedTextField
                            label="PESEL"
                            disabled={!employee.activeForCoaching}
                            value={employee.pesel}
                            fullWidth
                            error={!!validateEmployeeFields(employee).pesel}
                            helperText={validateEmployeeFields(employee).pesel}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].pesel = e.target.value
                                updatedEmployees[index] = updateComputedFields(updatedEmployees[index])
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <DatePicker
                            label="Data urodzenia"
                            disabled={!employee.activeForCoaching}
                            value={dayjs(employee.birthDate)}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].birthDate = e?.format('YYYY-MM-DD') || "";
                                validateAndSetEmployees(updatedEmployees);
                            }
                            }
                        />
                        <TextField
                            label="Płeć"
                            disabled={!employee.activeForCoaching}
                            value={employee.sex}
                            fullWidth
                            error={!!validateEmployeeFields(employee).sex}
                            helperText={validateEmployeeFields(employee).sex}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].sex = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <FormControl fullWidth>
                            <InputLabel id={`education-label-${index}`}>Wykształcenie</InputLabel>
                            <Select
                                labelId={`education-label-${index}`}
                                id={`education-${index}`}
                                value={employee.education}
                                label="Wykształcenie"
                                disabled={!employee.activeForCoaching}
                                onChange={(e) => {
                                    const updatedEmployees = [...employees];
                                    updatedEmployees[index].education = e.target.value;
                                    validateAndSetEmployees(updatedEmployees);
                                }}
                            >
                                {educationValues.map(option => (
                                    <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Kod pocztowy"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.zipCode}
                            fullWidth
                            error={!!validateEmployeeFields(employee).zipCode}
                            helperText={validateEmployeeFields(employee).zipCode}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.zipCode = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Miasto"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.city}
                            error={!!validateEmployeeFields(employee).city}
                            helperText={validateEmployeeFields(employee).city}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.city = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Ulica"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.streetName}
                            fullWidth
                            error={!!validateEmployeeFields(employee).streetName}
                            helperText={validateEmployeeFields(employee).streetName}
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.streetName = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Numer ulicy"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.streetNumber}
                            error={!!validateEmployeeFields(employee).streetNumber}
                            helperText={validateEmployeeFields(employee).streetNumber}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.streetNumber = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Województwo"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.voivodeship}
                            error={!!validateEmployeeFields(employee).voivodeship}
                            helperText={validateEmployeeFields(employee).voivodeship}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.voivodeship = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <HighlightedTextField
                            label="Powiat"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.district}
                            error={!!validateEmployeeFields(employee).district}
                            helperText={validateEmployeeFields(employee).district}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.district = e.target.value;
                                updatedEmployees[index] = updateComputedFields(updatedEmployees[index])
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <HighlightedTextField
                            label="Gmina"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.municipality}
                            error={!!validateEmployeeFields(employee).municipality}
                            helperText={validateEmployeeFields(employee).municipality}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.municipality = e.target.value;
                                updatedEmployees[index] = updateComputedFields(updatedEmployees[index])
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Kod gminy"
                            disabled={!employee.activeForCoaching}
                            value={employee.address.municipalityCode}
                            error={!!validateEmployeeFields(employee).municipalityCode}
                            helperText={validateEmployeeFields(employee).municipalityCode}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].address.municipalityCode = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Email"
                            disabled={!employee.activeForCoaching}
                            value={employee.email}
                            error={!!validateEmployeeFields(employee).email}
                            helperText={validateEmployeeFields(employee).email}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].email = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <TextField
                            label="Numer telefonu"
                            disabled={!employee.activeForCoaching}
                            value={employee.phoneNumber}
                            error={!!validateEmployeeFields(employee).phoneNumber}
                            helperText={validateEmployeeFields(employee).phoneNumber}
                            fullWidth
                            onChange={(e) => {
                                const updatedEmployees = [...employees];
                                updatedEmployees[index].phoneNumber = e.target.value;
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        />
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            onClick={() => {
                                const updatedEmployees = employees.filter((_, i) => i !== index);
                                validateAndSetEmployees(updatedEmployees);
                            }}
                        >
                            Usuń
                        </Button>
                    </Box>
                </Box>
            ))
            }
            <Button
                color="primary"
                variant="contained"
                fullWidth
                onClick={() => {
                    const updatedEmployees = [...employees, {
                        name: "",
                        surname: "",
                        pesel: "",
                        birthDate: "1900-01-01",
                        sex: "",
                        phoneNumber: "",
                        email: "",
                        education: "",
                        address: {
                            streetName: '',
                            streetNumber: '',
                            houseNumber: '',
                            city: '',
                            zipCode: '',
                            voivodeship: '',
                            municipality: '',
                            district: '',
                            municipalityCode: '',
                        },
                        employedSince: "2022-01-01",
                        employedUntil: "2022-01-01",
                        employedIndefinitely: false,
                        shiftCount: 0.0,
                        activeForCoaching: false,
                    }];
                    validateAndSetEmployees(updatedEmployees);
                }}>
                Dodaj Pracownika
            </Button>
        </Box >
    )
}

export default EmployeesList;